import cx from 'classnames';
import { IParticipantPerson } from 'Interfaces/participantPerson';
import { observer } from 'mobx-react';
import { useUIStore } from 'Modules/ui/index.store';
import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { focusContentEditable } from 'Utils/inputNodeUtils';
import AtMentionHereItemObserver from '../AtMentionHereItem';
import AtMentionMemberItemObserver from '../AtMentionMemberItem';
import { AtMentionMembersProps } from './interfaces';

const AtMentionMembers: React.FC<AtMentionMembersProps> = ({
  conversationId,
  loggedInPersonId,
  mentionListOpen,
  messageMentionFilter,
  onKeyDown,
  resolveConversationLinkPath,
  selectedMentionParticipantId,
  selectFilteredOtherParticipantPersonsInCurrentConversation,
  selectPersonPresenceStatus,
  selectUnreadCounts,
  setConversationAndTotalUnreadCount,
  updateMyLastReadMessage,
  className,
  insertMentionAtCursor,
  contentEditableRef,
  setMentionListOpen,
  onOpen,
  setSelectedMentionParticipantId,
  openUpwards = false,
}) => {
  const instanceId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const justOpenedRef = useRef<boolean>(false);
  const wasOpenRef = useRef<boolean>(false);
  const { mentionsActiveId, setMentionsActiveId } = useUIStore();

  const closeMentionList = useCallback(() => {
    setMentionListOpen(false);
    setSelectedMentionParticipantId(null);
  }, [setMentionListOpen, setSelectedMentionParticipantId]);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = mentionListOpen;

    if (!wasOpen && mentionListOpen) {
      justOpenedRef.current = true;
      setMentionsActiveId(instanceId);
      return;
    }

    if (mentionListOpen && mentionsActiveId === instanceId) {
      justOpenedRef.current = false;
    }

    if (wasOpen && !mentionListOpen) {
      justOpenedRef.current = false;

      if (mentionsActiveId === instanceId) {
        setMentionsActiveId(null);
      }
    }
  }, [instanceId, mentionListOpen, mentionsActiveId, setMentionsActiveId]);

  useEffect(() => {
    if (!mentionListOpen || !mentionsActiveId || justOpenedRef.current) {
      return;
    }

    if (mentionsActiveId !== instanceId) {
      closeMentionList();
    }
  }, [closeMentionList, instanceId, mentionListOpen, mentionsActiveId]);

  useEffect(() => {
    if (!mentionListOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;

      if (!target || rootRef.current?.contains(target)) {
        return;
      }

      closeMentionList();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [closeMentionList, mentionListOpen]);

  const onMentionItemClick = useCallback(
    (personIdStr: string) => {
      focusContentEditable(contentEditableRef.current, undefined, true);
      insertMentionAtCursor('@pr', `${personIdStr}`);
    },
    [contentEditableRef, insertMentionAtCursor]
  );

  const onHereClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      e.preventDefault();
      insertMentionAtCursor('@', 'here');
    },
    [insertMentionAtCursor]
  );

  const toggleVisibility = useCallback(() => {
    setMentionListOpen(!mentionListOpen);
    onOpen && onOpen();

    if (!mentionListOpen) {
      setSelectedMentionParticipantId('here');
    } else {
      setSelectedMentionParticipantId(null);
    }
  }, [
    mentionListOpen,
    onOpen,
    setMentionListOpen,
    setSelectedMentionParticipantId,
  ]);

  const isSelected = useCallback(
    (p: IParticipantPerson) => {
      return (
        (selectedMentionParticipantId === p.participant.id ||
          selectedMentionParticipantId ===
            p.participant.personId?.toString()) &&
        !!selectedMentionParticipantId
      );
    },
    [selectedMentionParticipantId]
  );

  const selectFilteredOther: (filterVal: string) => IParticipantPerson[] =
    selectFilteredOtherParticipantPersonsInCurrentConversation as unknown as (
      filterVal: string
    ) => IParticipantPerson[];

  const filteredOtherPtcPersons: IParticipantPerson[] =
    selectFilteredOther(messageMentionFilter);

  const mentionOptions = useMemo<IParticipantPerson[]>(
    () =>
      filteredOtherPtcPersons?.length > 0
        ? filteredOtherPtcPersons.filter(
            (p) =>
              p.person.sourceId !== loggedInPersonId.toString() &&
              !p.person.deleted
          )
        : [],
    [filteredOtherPtcPersons, loggedInPersonId]
  );

  return (
    <span ref={rootRef}>
      <Dropdown
        className={cx('mention-dropdown', className)}
        icon="at"
        onClick={toggleVisibility}
        open={mentionListOpen}
        onKeyDown={onKeyDown}
      >
        <Dropdown.Menu
          className={cx('mention-dropdown-menu', {
            'mention-dropdown-menu-flipped': openUpwards,
          })}
        >
          {openUpwards && <div className="top-triangle" />}
          <div className="mention-members-list menu scrolling">
            <AtMentionHereItemObserver
              key="here"
              isSelected={selectedMentionParticipantId === 'here'}
              conversationId={conversationId}
              resolveConversationLinkPath={resolveConversationLinkPath}
              onHereClick={onHereClick}
            />
            {mentionOptions.map((p) => (
              <AtMentionMemberItemObserver
                conversationId={conversationId}
                isSelected={isSelected(p)}
                key={p.participant.id}
                onMentionClick={onMentionItemClick}
                participantPerson={p}
                resolveConversationLinkPath={resolveConversationLinkPath}
                selectPersonPresenceStatus={selectPersonPresenceStatus}
                selectUnreadCounts={selectUnreadCounts}
                setConversationAndTotalUnreadCount={
                  setConversationAndTotalUnreadCount
                }
                updateMyLastReadMessage={updateMyLastReadMessage}
              />
            ))}
          </div>
          {!openUpwards && <div className="bottom-triangle" />}
        </Dropdown.Menu>
      </Dropdown>
    </span>
  );
};

export default observer(AtMentionMembers);
